import { z } from 'zod';
import { ProseOriginSchema } from './enums';
import { ThreatPatternIdSchema } from './ids';
import type { AttributeRefSchema } from './pack';
import { NonEmptyStringSchema } from './primitives';

/**
 * What a **question** says, as slots (docs/DECISIONS.md ZD-044).
 *
 * ## Derived from the question, not borrowed from the claim
 *
 * `FindingStatement` (ZD-043) and this are siblings and not the same shape, and
 * the differences were measured rather than assumed:
 *
 *  - **A finding has one subject; a question has two.** A finding's subject is
 *    also the object every ground clause is about, so one slot serves both. A
 *    question's subject and the object it asks about **come apart on 18 of the
 *    corpus's 165** — *"to decide whether this applies to Tool Bridge, we need
 *    three unrelated controls' scope"* — because an undetermined ref may name
 *    any object a navigation reached. Collapsing them is what produced a
 *    sentence whose subject is not the thing being asked about.
 *  - **`grounds` and `answer` are duals, not the same slot.** `grounds` renders
 *    a value that exists; `answer` renders the vocabulary a value must come
 *    from. A claim never needs the second and a question always does.
 *  - **`bearing` has no finding analogue at all.** A finding *is* the claim; a
 *    question has to say what turns on it.
 *  - **`threat_action` does not transfer.** A finding carries the pattern's
 *    authored `description` because that is what it asserts. A question carries
 *    the `label` only: quoting an authored paragraph into a question is noise,
 *    and the pattern's claim is not being made yet.
 *  - **`crossed_boundary`, `impacted_assets` and `control_direction` do not
 *    transfer.** All three are context for an assertion. A question directs no
 *    control and affects no asset; it has not established that anything is
 *    wrong.
 *
 * The load-bearing observation is that `PatternMatch.undetermined` and the
 * finding's `undecided` slot are **the same list read from opposite ends**. So
 * this is the inverse of one finding slot rather than a parallel grammar, which
 * is why it lives beside `FindingStatement` and shares its rules.
 *
 * ## The same three rules, carried across explicitly
 *
 * CLAUDE.md's clause about a rule not travelling between layers by being true
 * applies to this layer as much as it did to the last one, so each is named
 * with where it landed:
 *
 *  - **Three-valued stays three-valued.** `Ask.blocks_navigation` keeps *"this
 *    settles it"* apart from *"this advances it"*, because a `where` whose
 *    navigation is undetermined never evaluates its body.
 *  - **An absence stays a declaration.** `readability` is a slot rather than an
 *    omission: *"nothing in the documents says"* is unsayable over a document
 *    set with unread pages, and a renderer with no field for that would keep
 *    saying it (ZD-025).
 *  - **A claim stays distinguishable from its working.** `Ask.establishable_from`
 *    is the claim about answerability; the object, attribute and shape are the
 *    working, and they are present whatever it says.
 *
 * ## It is a derivation, and it mints nothing
 *
 * Built from the pinned pack, the persisted model and the evaluation's own
 * outcome; not persisted beside the clarification. Every slot is `rendered` and
 * none is authorable — a question naming a reworded attribute resolves to
 * nothing in the reviewer UI, which is `SLOT_MODEL_AUTHORABLE`'s argument for
 * refusing `undecided`, and it applies here to every slot without exception.
 */

export const CLARIFICATION_SLOT_KINDS = [
  /** The canonical object the predicate was evaluated against. */
  'subject',
  /** The pattern this would decide, and whether a finding was already raised. */
  'bearing',
  /** What is being asked: one entry per undetermined attribute. */
  'asks',
  /** Artefacts we could not read in full, where any exist. */
  'readability',
] as const;
export const ClarificationSlotKindSchema = z.enum(CLARIFICATION_SLOT_KINDS);
export type ClarificationSlotKind = z.infer<typeof ClarificationSlotKindSchema>;

/**
 * Whether a conditional finding is already in front of the reviewer.
 *
 * Read straight off the pattern's `unknown_policy`, which is required and has
 * no default (ZD-029 §4). The two are **different things for a client to
 * hear** — 54 of the corpus's 165 questions sit beside a conditional finding
 * that has already been raised on this pattern and subject, and 111 have
 * nothing at all behind them. The rendered sentence was identical for both.
 */
export const CLARIFICATION_STANDINGS = ['conditional_finding', 'nothing_raised'] as const;
export const ClarificationStandingSchema = z.enum(CLARIFICATION_STANDINGS);
export type ClarificationStanding = z.infer<typeof ClarificationStandingSchema>;

/**
 * Whether a document could carry this fact at all.
 *
 * `document` — the attribute is in `EXTRACTED_ATTRIBUTES` for its object type,
 * so a document stating it would have reached the model. `derivation` — it is
 * not, because the pipeline computes it: `data_flow.crosses_boundary_ids` is
 * derived from `TrustBoundary.separates` (ZD-027 rule 3), so asking a client
 * for it asks for something no document can give and no client can answer.
 *
 * **Not a proxy for whether the client knows.** That is not derivable from
 * anything in the model and would be invented, which is the line ZD-044 §4
 * draws.
 */
export const ANSWER_SOURCES = ['document', 'derivation'] as const;
export const AnswerSourceSchema = z.enum(ANSWER_SOURCES);
export type AnswerSource = z.infer<typeof AnswerSourceSchema>;

/**
 * What an answer has to look like, from the attribute's declared shape.
 *
 * `vocabulary` is non-null exactly where the shape is closed, so a question can
 * offer the values instead of naming an identifier and hoping. `holds` names
 * the object types a list of references holds, which is what turns
 * *"asset_ids"* into *"which data items"*.
 */
export const AnswerShapeSchema = z.object({
  kind: z.enum(['scalar', 'collection', 'enum_collection', 'reference', 'text', 'unknown_shape']),
  vocabulary: z.array(z.union([z.string(), z.boolean()])).nullable(),
  holds: z.array(z.string()),
});
export type AnswerShape = z.infer<typeof AnswerShapeSchema>;

/** One fact the evaluation could not read, and what answering it would take. */
export const AskSchema = z.object({
  /**
   * The object the question is about — **not necessarily the subject**, and
   * that is the whole reason this is a separate field.
   */
  object_id: z.string(),
  attribute: NonEmptyStringSchema,
  answer: AnswerShapeSchema,
  establishable_from: AnswerSourceSchema,
  /**
   * True where this attribute is read by a navigation, so an undetermined value
   * stopped the evaluation before the subtree behind it (ZD-044 §3).
   *
   * Conservative by construction: an attribute read both ways in one pattern is
   * blocking, because evaluation does not record which read produced the ref
   * and the weaker claim is the safe one.
   */
  blocks_navigation: z.boolean(),
});
export type Ask = z.infer<typeof AskSchema>;

const ClarificationSlotBase = {
  state: z.enum(['established', 'not_applicable']),
  origin: ProseOriginSchema,
};

export const ClarificationSlotSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('subject'), ...ClarificationSlotBase, object_id: z.string() }),
  z.object({
    kind: z.literal('bearing'),
    ...ClarificationSlotBase,
    threat_pattern_id: ThreatPatternIdSchema,
    /** The pattern's `title`, verbatim. Never its `description` — see above. */
    label: z.string(),
    standing: ClarificationStandingSchema,
  }),
  z.object({ kind: z.literal('asks'), ...ClarificationSlotBase, asks: z.array(AskSchema) }),
  z.object({
    kind: z.literal('readability'),
    ...ClarificationSlotBase,
    /**
     * Artefacts with at least one page that produced no citable unit, and which
     * pages those were.
     *
     * From `CanonicalModel.source_coverage`, which ZD-025 made a first-class
     * typed record for exactly this: *"we read at least one page of it"* had
     * been read as *"we read this document"*. A question asserting that nothing
     * in the documents says X, over a document set we only partly read, is that
     * same mistake in a client-facing sentence.
     *
     * **The pages travel with the artefact**, because ZD-025's own standard for
     * what this has to make sayable is *"pages 4–7 could not be read"* — an
     * artefact id alone tells a client a document was short-read without
     * telling them where to look.
     */
    unread: z.array(
      z.object({ artefact_id: NonEmptyStringSchema, pages: z.array(z.int().positive()) }),
    ),
  }),
]);
export type ClarificationSlot = z.infer<typeof ClarificationSlotSchema>;

function clarificationSlotIsPopulated(slot: ClarificationSlot): boolean {
  switch (slot.kind) {
    case 'subject':
      return slot.object_id !== '';
    case 'bearing':
      return slot.label !== '';
    case 'asks':
      return slot.asks.length > 0;
    case 'readability':
      return slot.unread.length > 0;
  }
}

/**
 * The clarification statement.
 *
 * **Two states, not three, and the missing one is the argument.**
 * `FindingStatement` needs `unestablished` because a finding routinely says
 * *nobody stated this*. A question is made **of** the unestablished — every ask
 * is one — so a slot here is either carrying something or does not apply. There
 * is no third case and inventing one would give an absence somewhere to hide.
 */
export const ClarificationStatementSchema = z
  .object({
    threat_pattern_id: ThreatPatternIdSchema,
    subject_id: z.string(),
    slots: z.array(ClarificationSlotSchema),
  })
  .superRefine((statement, ctx) => {
    const seen = new Set<ClarificationSlotKind>();
    statement.slots.forEach((slot, i) => {
      if (seen.has(slot.kind)) {
        ctx.addIssue({
          code: 'custom',
          message: `duplicate slot ${slot.kind}`,
          path: ['slots', i, 'kind'],
        });
      }
      seen.add(slot.kind);

      const populated = clarificationSlotIsPopulated(slot);
      if (slot.state === 'established' ? !populated : populated) {
        ctx.addIssue({
          code: 'custom',
          message:
            slot.state === 'established'
              ? `slot ${slot.kind} is established and carries nothing`
              : `slot ${slot.kind} is not_applicable and carries content — an absence must not ` +
                `be able to dress itself as a claim`,
          path: ['slots', i],
        });
      }

      /*
       * Nothing here may be authored, and the rule is stricter than the
       * finding's rather than inherited from it. A finding has two authorable
       * slots because a model may contextualise a claim; a question is entirely
       * object ids, attribute names and pack-authored labels, and every one of
       * them resolves to nothing if reworded (ZD-043 §6, applied).
       */
      if (slot.origin === 'authored') {
        ctx.addIssue({
          code: 'custom',
          message:
            `slot ${slot.kind} may not be authored: a question is made of identifiers and ` +
            `verbatim pack text, and a reworded one resolves to nothing`,
          path: ['slots', i, 'origin'],
        });
      }
    });

    /* Every slot, every time — a missing slot and a `not_applicable` one are
     * different claims, and only the second says anything. */
    for (const kind of CLARIFICATION_SLOT_KINDS) {
      if (seen.has(kind)) continue;
      ctx.addIssue({ code: 'custom', message: `statement omits slot ${kind}`, path: ['slots'] });
    }

    /*
     * A question with nothing to ask is not a question. `PatternMatch` already
     * refuses an `unknown` verdict that names nothing (ZD-043 §3); this is the
     * same rule where the population is 111 clarifications with no match behind
     * them at all, which that refinement cannot see.
     */
    const asks = statement.slots.find((slot) => slot.kind === 'asks');
    if (asks?.kind === 'asks' && asks.asks.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'a clarification must name at least one attribute it could not establish',
        path: ['slots'],
      });
    }
  });
export type ClarificationStatement = z.infer<typeof ClarificationStatementSchema>;

/** The slot of a given kind. Every statement carries all of them. */
export function clarificationSlotOf<K extends ClarificationSlotKind>(
  statement: ClarificationStatement,
  kind: K,
): Extract<ClarificationSlot, { kind: K }> {
  const slot = statement.slots.find((candidate) => candidate.kind === kind);
  if (!slot) throw new Error(`clarification statement has no ${kind} slot`);
  return slot as Extract<ClarificationSlot, { kind: K }>;
}

/**
 * Whether answering every ask **decides** the pattern, or only advances it
 * (ZD-044 §3).
 *
 * **Derived, never stored.** Two records of one fact can disagree and the
 * disagreement would be invisible (ZD-017) — and the fact is already on each
 * ask, where it belongs, because it is a property of how that attribute is read
 * rather than of the question as a whole.
 */
export function clarificationDecides(statement: ClarificationStatement): boolean {
  return clarificationSlotOf(statement, 'asks').asks.every((ask) => !ask.blocks_navigation);
}

/** The refs an `asks` slot stands for, for anything that still wants the list. */
export function asksAsRefs(
  statement: ClarificationStatement,
): z.infer<typeof AttributeRefSchema>[] {
  return clarificationSlotOf(statement, 'asks').asks.map((ask) => ({
    object_id: ask.object_id,
    attribute: ask.attribute,
  }));
}
